<template>
  <div
    data-test-id="data-table"
    transition-show="fade-in"
    transition-hide="fade-out"
  >
    <q-table
      ref="table"
      v-model:pagination="paginationSync"
      :rows="data"
      :columns="processedColumns"
      :row-key="rowKey"
      bordered
      flat
      wrap-cells
      binary-state-sort
      :no-data-label="`No ${title}`"
      :hide-pagination="!paginate"
      :rows-per-page-options="[5, 10, 15, 20, 25, 50]"
      :hide-bottom="!!data.length && !paginate"
      :loading="loading"
      @row-click="(_event, row, _index) => $emit('row-click', row)"
    >
      <template #top>
        <slot name="top">
          <slot name="prepend-header-controls" />
          <slot name="title">
            <div
              v-if="title"
              class="q-table__title row items-center"
            >
              {{ title }}
            </div>
          </slot>

          <q-space />

          <div class="row q-gutter-sm items-center">
            <slot name="header-controls" />
            <q-inner-loading :showing="loading" />
          </div>
        </slot>
      </template>

      <template #body-cell="props">
        <q-td
          :props="props"
          :style="{ 'max-width': props.col.maxWidth }"
        >
          <template v-if="props.col.loading">
            <slot
              name="column-loading"
              v-bind="props"
            >
              <q-spinner size="2em" />
            </slot>
          </template>
          <template v-else>
            <!-- RENDERERS -->
            <chip-cell
              v-if="props.col.component === 'chip'"
              :props="props"
              @filter="({ name, value }) => $emit('filter', { name, value })"
            />
            <date-time-cell
              v-else-if="props.col.component === 'date-time'"
              :props="props"
            />
            <icon-cell
              v-else-if="props.col.component === 'icon'"
              :value="props.value"
            />
            <money-cell
              v-else-if="props.col.component === 'money' || props.col.component === 'money-count'"
              :props="props"
            />
            <template v-else-if="props.col.component === 'custom-component'">
              <slot
                name="custom-component"
                v-bind="props"
              />
            </template>
            <component
              :is="props.col.component"
              v-else-if="props.col.component"
              v-bind="{ props: props }"
            />
            <div
              v-else
              class="text-column"
            >
              {{ props.value }}
            </div>
          </template>
        </q-td>
      </template>

      <template #body-cell-actions="props">
        <q-td
          :props="props"
        >
          <slot
            name="row-actions"
            v-bind="props"
          />
        </q-td>
      </template>
      <template #pagination="scope">
        <slot
          name="pagination"
          v-bind="scope"
        />
      </template>
    </q-table>
  </div>
</template>

<script>
import ChipCell from 'src/modules/admin/components/model-table/components/ChipCell'
import DateTimeCell from 'src/modules/admin/components/model-table/components/DateTimeCell'
import IconCell from 'src/modules/admin/components/model-table/components/IconCell'
import MoneyCell from 'src/modules/admin/components/model-table/components/MoneyCell'

const defaultColumnProps = { align: 'left', headerStyle: 'vertical-align: bottom' }

export default {
  name: 'DataTable',
  components: {
    ChipCell,
    DateTimeCell,
    IconCell,
    MoneyCell
  },
  configuration: ['currentSite', 'currentCircuit'],
  context: ['currentUser'],
  props: {
    data: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    columns: {
      type: Array,
      required: false,
      default: null
    },
    paginate: {
      type: Boolean,
      required: false,
      default: false
    },
    pagination: {
      type: Object,
      required: false,
      default: () => ({
        page: 1,
        rowsPerPage: 0
      })
    },
    rowKey: {
      type: String,
      required: false,
      default: 'id'
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['row-click', 'filter'],
  data () {
    return {
      paginationSync: this.pagination
    }
  },
  computed: {
    processedColumns () {
      const columns = _.map(this.columns, column => {
        if (column.hide) return null

        column = _.extend({}, defaultColumnProps, column)

        const key = (column.name || column.field)

        if (column.field === 'id') {
          column.label ||= 'ID'
        } else {
          column.label ||= _.startCase(key)
        }

        column.name ||= column.field

        return column
      })

      return _.compact(columns)
    }
  }
}
</script>
